import type { CardDetail, CardEdit, CardPricePoint, CardStockUpdate, CardSummary } from "@hobbyshopify/backend-client";
import { formatCents, gradeLabel } from "./card-display";
import { gradeKeyFromText } from "./grades";
import { centsToInput, inputToCents, isMoneyValid } from "./scan-edit";

/**
 * One card the shop sells, edited in place: its price, floor and count, what it
 * is, and what Save is about to do. Pure: the card page's controller holds the
 * fields, and these turn them into what Save sends. A port of the iOS
 * `AdminCardFields` and `AdminCardScreenModel`.
 */

/** What the card page's fields hold, as text where the merchant types. */
export interface CardFields {
  readonly price: string;
  /** Off: the floor follows the asking price, one figure for both. */
  readonly separateFloor: boolean;
  readonly floor: string;
  readonly onHand: number;
  readonly player: string;
  readonly year: string;
  readonly cardNumber: string;
  readonly setName: string;
  readonly parallel: string;
  readonly grade: string;
}

/** Why a count went down: stock that is gone, or a miscount found. */
export type StockLossKind = NonNullable<CardStockUpdate["kind"]>;

export const onHandOf = (detail: Pick<CardDetail, "lots">) => detail.lots.reduce((n, lot) => n + lot.quantity, 0);

/** Copies held for open quotes, which a count cannot go below. */
export const reservedOf = (detail: Pick<CardDetail, "lots">) =>
  detail.lots.reduce((n, lot) => n + lot.reservedQuantity, 0);

export function cardFields(card: CardSummary, onHand: number): CardFields {
  return {
    price: centsToInput(card.currentPriceCents),
    separateFloor: card.floorPriceCents != null && card.floorPriceCents !== card.currentPriceCents,
    floor: centsToInput(card.floorPriceCents ?? card.currentPriceCents),
    onHand,
    player: card.player,
    year: card.year === 0 ? "" : String(card.year),
    cardNumber: card.cardNumber,
    setName: card.setName ?? "",
    parallel: card.parallelType,
    grade: gradeLabel(card),
  };
}

/** The card's own values, which the fields start from: the count is its lots'. */
export const cardBase = (detail: CardDetail) => cardFields(detail.card, onHandOf(detail));

/** Two money fields say the same: the same figure however it is typed ("9", "9.00", "9,00"), or both blank. */
const sameMoney = (a: string, b: string) => {
  const cents = inputToCents(a);
  return a === b || (cents === undefined ? !a.trim() && !b.trim() : cents === inputToCents(b));
};

/** The year as Save sends it: "2021", " 2021 " and "02021" are 2021, and anything else none. */
const yearOf = (text: string) => {
  const year = text.trim();
  return /^\d+$/.test(year) ? Number(year) : 0;
};

/** A field says what the card says, as Save reads it: what it would send for one is no change. */
function sameForSave(key: keyof CardFields, a: string, b: string): boolean {
  if (key === "price" || key === "floor") return sameMoney(a, b);
  if (key === "year") return yearOf(a) === yearOf(b);
  return key === "grade" ? a === b : a.trim() === b.trim();
}

/**
 * Every field the merchant left alone takes the card's new value, so a reload
 * never overwrites what was typed and never leaves the rest stale. While the
 * fields can be typed in, left alone is the very text the page wrote: "120" on
 * its way to "1205" is the merchant's, though it says the card's figure. Held
 * for a Save, a field is left alone while it says what the card it was typed
 * over says, as Save reads it: a figure typed back in another form is the
 * card's. The floor's switch and figure are one choice: kept as typed once
 * either was touched, and kept beside a typed price while only the card's price
 * moved, so a floor that follows the price follows the one typed.
 */
export function rebasedFields(typed: CardFields, was: CardFields, now: CardFields, held = false): CardFields {
  const same = <K extends keyof CardFields>(key: K, a: CardFields[K], b: CardFields[K]) =>
    held && typeof a === "string" && typeof b === "string" ? sameForSave(key, a, b) : a === b;
  const out: Record<string, unknown> = { ...typed };
  for (const key of Object.keys(typed) as (keyof CardFields)[]) {
    if (same(key, typed[key], was[key])) out[key] = now[key];
  }
  const floorTouched = typed.separateFloor !== was.separateFloor || !same("floor", typed.floor, was.floor);
  if (floorTouched || (!same("price", typed.price, was.price) && now.floor === was.floor)) {
    out.separateFloor = typed.separateFloor;
    out.floor = typed.floor;
  }
  return out as unknown as CardFields;
}

/**
 * A grade as the card stores it: "PSA 9" is PSA and 9, "BGS 9.5" BGS and 9.5,
 * "Raw" or blank no grader and 0. Null for text the grade list cannot read.
 */
export function gradeParts(text: string): { gradingCompany: string; grade: number } | null {
  if (!text.trim()) return { gradingCompany: "", grade: 0 };
  const key = gradeKeyFromText(text);
  if (key === null) return null;
  if (key === "raw") return { gradingCompany: "", grade: 0 };
  const [company, ...rest] = key.split("_");
  const numbers = rest.filter((part) => /^\d+$/.test(part)).map(Number);
  // The gold labels carry no number: BGS's is a 9.5, SGC's a 10.
  const grade =
    numbers.length === 0
      ? company === "bgs"
        ? 9.5
        : 10
      : numbers.length === 1
        ? numbers[0]
        : numbers[0] + numbers[1] / 10;
  return { gradingCompany: company.toUpperCase(), grade };
}

const priceCentsOf = (fields: CardFields) => inputToCents(fields.price) ?? null;

/** The floor Save sends: its own when separate, else the asking price. */
export const floorCentsOf = (fields: CardFields) =>
  fields.separateFloor ? (inputToCents(fields.floor) ?? null) : priceCentsOf(fields);

export const priceError = (fields: CardFields) =>
  isMoneyValid(fields.price) ? null : "Enter a price, or leave it blank.";

export function floorError(fields: CardFields): string | null {
  if (!fields.separateFloor) return null;
  if (!isMoneyValid(fields.floor)) return "Enter a price, or leave it blank.";
  const floor = floorCentsOf(fields);
  const price = priceCentsOf(fields);
  return floor != null && price != null && floor > price ? "The floor cannot be above the asking price." : null;
}

/**
 * A floor that follows the price counts as changed only when the card had a
 * separate one before. Text that is not a price is a change too, one to put
 * right before Save, never dropped as if nothing had been typed.
 */
export function pricesChanged(fields: CardFields, card: CardSummary): boolean {
  if (!isMoneyValid(fields.price) || (fields.separateFloor && !isMoneyValid(fields.floor))) return true;
  if (priceCentsOf(fields) !== card.currentPriceCents) return true;
  if (fields.separateFloor) return floorCentsOf(fields) !== card.floorPriceCents;
  return card.floorPriceCents != null && card.floorPriceCents !== card.currentPriceCents;
}

/** A count on an untracked card means something only once it is above zero: that starts the counting. */
export const stockChanged = (fields: CardFields, base: CardFields, card: CardSummary) =>
  fields.onHand !== base.onHand && (card.trackStock || fields.onHand > 0);

export const countDropped = (fields: CardFields, base: CardFields, card: CardSummary) =>
  stockChanged(fields, base, card) && fields.onHand < base.onHand;

export function yearError(fields: CardFields): string | null {
  const text = fields.year.trim();
  if (!text) return null;
  const year = /^\d+$/.test(text) ? Number(text) : Number.NaN;
  return year >= 1800 && year <= 2100 ? null : "Enter a four-digit year.";
}

/** Only a grade the merchant typed: the card's own stands even when the list cannot read it. */
export function gradeError(fields: CardFields, base: CardFields): string | null {
  if (fields.grade === base.grade || gradeParts(fields.grade) !== null) return null;
  return "Pick a grade from the list, or type one like PSA 9.";
}

/** Only a name the merchant changed: a card stored with none, a team card say, still saves its price and count. */
export const playerError = (fields: CardFields, base: CardFields) =>
  fields.player.trim() === base.player.trim() || fields.player.trim() ? null : "A card needs a name.";

/**
 * Only what differs from the card, so an untouched field is never sent. The card's own text is read trimmed, as a
 * field is: one stored with a space around it is no edit, which the server would take for a new card and reprice.
 */
export function cardIdentityEdit(fields: CardFields, card: CardSummary, base: CardFields): CardEdit {
  const edit: CardEdit = {};
  const player = fields.player.trim();
  if (player !== card.player.trim()) edit.player = player;
  const year = yearOf(fields.year);
  if (year !== card.year) edit.year = year;
  const cardNumber = fields.cardNumber.trim();
  if (cardNumber !== card.cardNumber.trim()) edit.cardNumber = cardNumber;
  const setName = fields.setName.trim();
  if (setName !== (card.setName ?? "").trim()) edit.setName = setName;
  const parallel = fields.parallel.trim();
  if (parallel !== card.parallelType.trim()) edit.parallelType = parallel;
  const grade = fields.grade === base.grade ? null : gradeParts(fields.grade);
  if (grade) {
    edit.gradingCompany = grade.gradingCompany;
    edit.grade = grade.grade;
    edit.slabbed = grade.gradingCompany !== "";
  }
  return edit;
}

export const isEmptyEdit = (edit: CardEdit) => Object.keys(edit).length === 0;

/** The server keeps what a card is case-blind, so a recased name is written but is the same card. */
const sameText = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/** Everything but the set name makes it a different card, which clears its comps; Save then looks the price up again. */
export const changesIdentity = (edit: CardEdit, card: CardSummary) =>
  (edit.player !== undefined && !sameText(edit.player, card.player)) ||
  (edit.year !== undefined && edit.year !== card.year) ||
  (edit.cardNumber !== undefined && !sameText(edit.cardNumber, card.cardNumber)) ||
  (edit.parallelType !== undefined && !sameText(edit.parallelType, card.parallelType)) ||
  (edit.gradingCompany !== undefined &&
    (!sameText(edit.gradingCompany, card.gradingCompany) ||
      edit.grade !== card.grade ||
      edit.slabbed !== card.slabbed));

/** The card page's edit, as the Save bar reads it. */
export interface CardEditing {
  readonly card: CardSummary;
  /** The card's own values, which the fields started from. */
  readonly base: CardFields;
  readonly fields: CardFields;
  readonly lossKind: StockLossKind;
  readonly lossNote: string;
}

export function isDirty(e: CardEditing): boolean {
  return (
    !isEmptyEdit(cardIdentityEdit(e.fields, e.card, e.base)) ||
    pricesChanged(e.fields, e.card) ||
    stockChanged(e.fields, e.base, e.card)
  );
}

/**
 * Whether Save may run, leaving out whether the role edits and whether a save
 * is under way. The price rules hold only a save that sends prices: a reprice
 * moves the price and leaves the floor, which must not lock the count.
 */
export function isSaveable(e: CardEditing): boolean {
  return (
    isDirty(e) &&
    (!pricesChanged(e.fields, e.card) || (isMoneyValid(e.fields.price) && floorError(e.fields) === null)) &&
    yearError(e.fields) === null &&
    gradeError(e.fields, e.base) === null &&
    playerError(e.fields, e.base) === null
  );
}

/** Where a Stock row's menu opens the card: Price and stock, or Price history. */
export type CardSection = "price" | "history";

/** Calls on the card that threw on their way: each may have gone through, so the card is read again. */
export const CARD_SAVE_FAILED_ON_THE_WAY =
  "Something went wrong while saving. The card shows what was saved: check it before saving again.";
export const CARD_SAVE_FAILED_UNREAD =
  "Something went wrong while saving, and the card could not be read again. Reload the page to see what was saved.";
/** Save's writes went through and the card could not be read after them. */
export const CARD_SAVED_UNREAD = "Saved, but the card could not be read again. Reload the page to see it.";
/** A Save's sentence already says the card could not be read: a read's own failure is not said beside it. */
export const saysUnread = (sentence: string | null) =>
  sentence === CARD_SAVED_UNREAD || sentence === CARD_SAVE_FAILED_UNREAD;
/** A change another page made, landing under a Save, left it one the rules refuse: nothing was sent. */
export const CARD_CHANGED_BEFORE_SAVE =
  "The card changed on another page before your changes could be saved. Check them, then save again.";
export const REPRICE_FAILED_ON_THE_WAY = "Something went wrong while repricing. Try again.";
export const ARCHIVE_FAILED_ON_THE_WAY = "Something went wrong while archiving. Try again if the card is still here.";

export const saveTitle = (e: CardEditing) =>
  changesIdentity(cardIdentityEdit(e.fields, e.card, e.base), e.card) ? "Save and reprice" : "Save";

/** What Save is about to do, in one line over the bar. */
export function changeLine(e: CardEditing, shown: CardFields = e.base): string {
  const parts: string[] = [];
  if (!isEmptyEdit(cardIdentityEdit(e.fields, e.card, e.base))) parts.push("Card details");
  // A price or floor Save cannot send is named under its field, not as a figure here.
  if (pricesChanged(e.fields, e.card) && isMoneyValid(e.fields.price) && floorError(e.fields) === null) {
    const money = (cents: number | null) => (cents == null ? "none" : formatCents(cents));
    const price = priceCentsOf(e.fields);
    const floor = floorCentsOf(e.fields);
    const priceMoves = price !== e.card.currentPriceCents;
    if (priceMoves) parts.push(`Price ${money(e.card.currentPriceCents)} → ${money(price)}`);
    // A floor that followed the price and still does goes without saying. When Save reprices a price
    // left alone, the lookup sets it, and a floor that follows it.
    const followed = e.card.floorPriceCents == null || e.card.floorPriceCents === e.card.currentPriceCents;
    if (floor !== e.card.floorPriceCents && (e.fields.separateFloor || !followed)) {
      const looked = !e.fields.separateFloor && !priceMoves && saveTitle(e) === "Save and reprice";
      parts.push(`Floor ${money(e.card.floorPriceCents)} → ${looked ? "the new price" : money(floor)}`);
    }
  }
  const stock = stockUpdate(e, shown);
  if (stock !== null) {
    const reason =
      stock.kind === "SHRINKAGE" ? ", lost or damaged" : countDropped(e.fields, e.base, e.card) ? ", correction" : "";
    parts.push(`Stock ${e.base.onHand} → ${e.fields.onHand}${reason}`);
  }
  return parts.join(" · ");
}

/**
 * The count Save sends, or null when it has not moved. A loss is one the
 * merchant was asked about: a drop on the card as the page showed it, and on
 * the card as it is.
 */
export function stockUpdate(e: CardEditing, shown: CardFields = e.base): CardStockUpdate | null {
  if (!stockChanged(e.fields, e.base, e.card)) return null;
  const dropped = countDropped(e.fields, e.base, e.card) && e.fields.onHand < shown.onHand;
  const reason =
    e.lossNote.trim() || (dropped && e.lossKind === "SHRINKAGE" ? "Lost or damaged" : "Counted on the card");
  return { quantity: e.fields.onHand, kind: dropped ? e.lossKind : "ADJUSTED", reason };
}

/** Under the count: why it cannot go lower, or how to start counting. */
export function stockHint(card: CardSummary, reserved: number): string | null {
  if (!card.trackStock) return "Set a count to start tracking this card.";
  if (reserved <= 0) return null;
  return `${reserved} held for open customer ${reserved === 1 ? "quote" : "quotes"}, so the count cannot go below that.`;
}

/** "1976 · Topps · #148": year, brand and number, under the photos. */
export function cardYearBrandNumber(card: CardSummary): string {
  const number = card.cardNumber.trim().replace(/^#/, "");
  return [card.year ? String(card.year) : "", card.brand.trim(), number ? `#${number}` : ""]
    .filter(Boolean)
    .join(" · ");
}

/** The shop's price and the one listed online differ: the listing has not caught up. */
export function priceOutOfSync(detail: CardDetail): boolean {
  const mine = detail.card.currentPriceCents;
  const theirs = detail.providerPriceCents;
  return mine != null && theirs != null && mine !== theirs;
}

/** Lots summed per location, in the order they first appear. */
export function stockByLocation(detail: CardDetail): { name: string; quantity: number }[] {
  const totals = new Map<string, number>();
  for (const lot of detail.lots) {
    const name = lot.locationName?.trim() || "No location";
    totals.set(name, (totals.get(name) ?? 0) + lot.quantity);
  }
  return [...totals].map(([name, quantity]) => ({ name, quantity }));
}

const PRICE_KIND_LABELS: Record<CardPricePoint["kind"], string> = {
  ESTIMATED: "Estimated",
  SET: "Asking",
  REPRICED: "Repriced",
};

export const priceKindLabel = (kind: CardPricePoint["kind"]) => PRICE_KIND_LABELS[kind] ?? "Price";

/** The newest few, newest first: the API lists them oldest first. */
export const recentPrices = (points: readonly CardPricePoint[], count = 5) => [...points].reverse().slice(0, count);
